import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDrag } from 'react-dnd';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import Icon from '../../misc/Icon';

const StyledItem = styled.div` 
  display: flex; 
  flex-direction: row;
  align-items: center;
  border: ${(props) => (props.focused ? `1px solid ${props.theme.primaryColor}` : `1px solid ${props.theme.borderColor}`)};
  border-radius: ${(props) => props.theme.borderRadius}px;
  padding: 6px;
  transition: all 0.15s ease-in-out;
  :hover {
    border: 1px solid ${(props) => props.theme.primaryColor};
    cursor: grab;
    svg {
      color: ${(props) => props.theme.primaryColor};
    }
  };
`;

const TableEditorCell = ({
  id,
  activeFieldPosition,
  focused,
  type,
  displayType,
  columnType,
  columnId,
  rowIndex,
  targetValue, //Target Option Id from Dropdown Column Options
  width = 100,
  height = 50,
  disableDragAndDrop
}) => {

  const theme = useContext(ThemeContext);

  /**
   * Important Note:
   *
   * activeFieldPosition Explanation
   *
   * We need the activeField for table type fields because there can be many fieldPostions for a single table field,
   * we link the all the associated fieldPostions back to the original field using the `fieldPosition.field` property.
   *
   * How can multiple fieldPositions be associated with a single table field?
   *
   * When there are many fieldPositions for the same field they are each pointing at different individual cells or event cell optional
   * in the case of a dropdown cell. The target cell is determined by columnId and rowIndex.
   */
  const tableCellFieldPostion = {
    ...activeFieldPosition,
    type,
    displayType,
    column: columnId,
    columnType,
    rowIndex,
    targetValue,
    width,
    height,
    padding: 0
  };

  const [collectedProps, drag] = useDrag({
    type,
    item: tableCellFieldPostion
  });

  return (
    <StyledItem
      theme={theme}
      ref={disableDragAndDrop ? null : drag}
      {...collectedProps}
      data-testid={`${id}-${rowIndex}-${columnId}-table-editor-cell`}
      focused={focused}
    >
      <Icon
        icon={ThemeIcons.misc.drag}
        size="sm"
        color={theme.borderColor}
        hoverColor={theme.primaryColor}
        hoverCursor="grab"
      />
    </StyledItem>
  );

};

TableEditorCell.propTypes = {
  id: PropTypes.string.isRequired,
  field: PropTypes.string,
  type: PropTypes.string.isRequired,
  displayType: PropTypes.string.isRequired,
  columnId: PropTypes.string.isRequired,
  rowId: PropTypes.string.isRequired,
  rowIndex: PropTypes.number.isRequired,
  targetValue: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default TableEditorCell;
